import React, { useEffect, useState } from 'react';
import style from './InsiderTrades.module.css';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import useInsiderTradings from '../../hooks/useInsiderTradings';
import OpenMarketTradesInfoTooltip from '../OpenMarketTradesInfoTooltip/OpenMarketTradesInfoTooltip';

function openMarketOnlyFromUrl() {
  if (typeof window === 'undefined') {
    return false;
  }
  return new URLSearchParams(window.location.search).get('open_market') === '1';
}

const InsiderTradesTab = ({ stock }) => {
  const [openMarketOnly, setOpenMarketOnly] = useState(false);
  const { insiderTradings, isLoading } = useInsiderTradings(stock.id, { openMarketOnly });

  useEffect(() => {
    setOpenMarketOnly(openMarketOnlyFromUrl());
  }, []);

  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  const tradesListHref = `/insider-trades/${stock.symbol.toLowerCase()}${openMarketOnly ? '?open_market=1' : ''}`;

  const filterBar = (
    <div className={style.openMarketFilter}>
      <label className={style.openMarketLabel}>
        <div className="checkbox">
            <input
              type="checkbox"
              autoComplete="off"
              checked={openMarketOnly}
              onChange={(event) => setOpenMarketOnly(event.target.checked)}
            />
          <span className="checkbox__box" />
        </div>
        <span>Open-market trades only</span>
        <OpenMarketTradesInfoTooltip />
      </label>
    </div>
  );

  if (!insiderTradings) {
    return (
      <div>
        {filterBar}
        {isLoading ? <p>Loading insider trades…</p> : <p>No insider trades available</p>}
      </div>
    );
  }

  // create labels for the last 6 quarters
  const labels = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date();
    date.setMonth(date.getMonth() - i * 3);
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    const year = date.getFullYear();
    labels.push(`Q${quarter} ${year}`);
  }

  const dataSell = [];
  const dataBuy = [];
  const buyCount = [];
  const sellCount = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date();
    const month = date.getMonth() - i * 3;
    date.setMonth(month - (month % 3));
    date.setDate(1);

    const insiderTrading = insiderTradings.find((insiderTrading) => {
      const insiderTradingDate = new Date(insiderTrading.quarter);
      return (
        insiderTradingDate.getFullYear() === date.getFullYear() &&
        insiderTradingDate.getMonth() === date.getMonth() &&
        insiderTradingDate.getDate() === date.getDate()
      );
    });
    if (insiderTrading) {
      dataSell.push(insiderTrading.sum_disposition);
      dataBuy.push(insiderTrading.sum_acquistion);
      buyCount.push(insiderTrading.count_acquistion);
      sellCount.push(insiderTrading.count_disposition);
    } else {
      dataSell.push(0);
      dataBuy.push(0);
      buyCount.push(0);
      sellCount.push(0);
    }
  }

  const data = {
    labels,
    datasets: [
      {
        label: 'Shares Sold',
        data: dataSell,
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        type: 'bar',
      },
      {
        label: 'Shares Bought',
        data: dataBuy,
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(60, 157, 14, 0.5)',
        type: 'bar',
      },
    ],
  };
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        intersect: false,
        mode: 'index',
        callbacks: {
          afterLabel: (context) => {
            let count;
            if (context.dataset.label === 'Shares Bought') {
              count = buyCount[context.dataIndex];
            } else {
              count = sellCount[context.dataIndex];
            }
            return `by ${count} insiders`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
        ticks: {
          beginAtZero: true,
        },
      },
    },
  };

  return (
    <div>
      {filterBar}
      {!dataSell.some((value, index) => value > 0 || dataBuy[index] > 0) ?
        <p className={style.noData}>No insider trades available</p>
        :
        <div className="utilities__text-center">
          <Bar options={options} data={data} />
          <a href={tradesListHref} className={style.link}>View list of trades</a>
        </div>
      }
    </div>
  );
};

export default InsiderTradesTab;
